import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ScrollIndicator from "./scroll-indicator.component.jsx";

const TARGETS = [
  { selector: ".job-detail-actions-menu .ant-dropdown-menu", axis: "y" },
  { selector: ".production-status-scroll .ant-dropdown-menu", axis: "y" },
  { selector: ".ant-table-body", axis: "y" },
  { selector: ".ant-modal-body", axis: "y" },
  { selector: ".ant-drawer-body", axis: "y" },
  { selector: ".dashboard-card .ant-card-body", axis: "y" },
  { selector: ".eula-markdown-card", axis: "y" },
  { selector: ".schedule-job-modal", axis: "y" },
  { selector: ".report-center-scroll", axis: "y" },
  { selector: ".tech-job-clock-scroll", axis: "y" },
  { selector: ".csi-scroll-area", axis: "y" },
  { selector: ".chat-send-message", axis: "y" },
  { selector: ".chat-sender-input textarea", axis: "y" },
  { selector: ".chat-affix-keyboard-open .chat-title", axis: "y" },
  { selector: ".media-gallery-toolbar-scroll", axis: "x" },
  { selector: ".ant-table-content", axis: "x" },
  { selector: ".ant-table-body", axis: "x" },
  { selector: ".dms-log-horizontal-scroll", axis: "x" },
  { selector: ".kanban-settings-section-card .ant-card-body", axis: "x" }
];

let nextTargetId = 1;
const targetIds = new WeakMap();

const getTargetId = (element) => {
  if (!targetIds.has(element)) targetIds.set(element, nextTargetId++);
  return targetIds.get(element);
};

const sameTargets = (left, right) =>
  left.length === right.length &&
  left.every(
    (entry, index) =>
      entry.element === right[index].element &&
      entry.shell === right[index].shell &&
      entry.axis === right[index].axis &&
      JSON.stringify(entry.trackStyle) === JSON.stringify(right[index].trackStyle)
  );

export default function ScrollIndicatorPortalTargets() {
  const [matches, setMatches] = useState([]);
  const scheduleScanRef = useRef(null);

  useEffect(() => {
    let animationFrame;

    const scan = () => {
      animationFrame = undefined;
      const seen = new Set();
      const nextMatches = TARGETS.flatMap(({ selector, axis }) =>
        Array.from(document.querySelectorAll(selector)).flatMap((element) => {
          const matchKey = `${getTargetId(element)}-${axis}`;
          if (seen.has(matchKey)) return [];
          seen.add(matchKey);
          const shell = element.parentElement;
          if (!shell) return [];
          const elementRect = element.getBoundingClientRect();
          const shellRect = shell.getBoundingClientRect();
          const trackStyle =
            axis === "x"
              ? {
                  left: elementRect.left - shellRect.left + shell.scrollLeft,
                  top: elementRect.bottom - shellRect.top + shell.scrollTop - 12,
                  width: element.clientWidth
                }
              : {
                  top: elementRect.top - shellRect.top + shell.scrollTop,
                  right: shellRect.right - elementRect.right - shell.scrollLeft,
                  height: element.clientHeight
                };
          return [{ axis, element, id: matchKey, shell, trackStyle }];
        })
      );
      setMatches((current) => (sameTargets(current, nextMatches) ? current : nextMatches));
    };

    const scheduleScan = () => {
      if (animationFrame === undefined) animationFrame = window.requestAnimationFrame(scan);
    };
    scheduleScanRef.current = scheduleScan;

    scan();
    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleScan, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", scheduleScan);
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      scheduleScanRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || matches.length === 0) return undefined;

    const resizeObserver = new ResizeObserver(() => scheduleScanRef.current?.());
    const observedElements = new Set();
    matches.forEach(({ element, shell }) => {
      if (!observedElements.has(element)) {
        resizeObserver.observe(element);
        observedElements.add(element);
      }
      if (!observedElements.has(shell)) {
        resizeObserver.observe(shell);
        observedElements.add(shell);
      }
    });

    return () => resizeObserver.disconnect();
  }, [matches]);

  useEffect(() => {
    matches.forEach(({ axis, element, shell }) => {
      const computedStyle = window.getComputedStyle(element);
      element.style.setProperty(
        axis === "x" ? "--scroll-indicator-base-padding-bottom" : "--scroll-indicator-base-padding-right",
        axis === "x" ? computedStyle.paddingBottom : computedStyle.paddingRight
      );
      element.classList.add("scroll-indicator-managed", `scroll-indicator-managed-${axis}`);
      if (window.getComputedStyle(shell).position === "static") {
        shell.classList.add("scroll-indicator-managed-shell-static");
      }
    });

    return () => {
      matches.forEach(({ axis, element, shell }) => {
        element.classList.remove("scroll-indicator-managed", `scroll-indicator-managed-${axis}`);
        element.style.removeProperty(
          axis === "x" ? "--scroll-indicator-base-padding-bottom" : "--scroll-indicator-base-padding-right"
        );
        shell.classList.remove("scroll-indicator-managed-shell-static");
      });
    };
  }, [matches]);

  return matches.map(({ axis, element, id, shell, trackStyle }) =>
    createPortal(
      <ScrollIndicator
        key={id}
        axis={axis}
        className={`scroll-indicator-portal scroll-indicator-portal--${axis}`}
        observeMutations
        style={trackStyle}
        targetElement={element}
      />,
      shell
    )
  );
}
